// src/components/Sparkline.jsx
//
// Same visual language as the Today screen's Health Score chart (gradient
// fill under the line, a glowing latest point, hollow dots for earlier
// draws) so a marker trend reads as the same product wherever it appears —
// the Current Markers tab (click a marker to see its trend) and anywhere
// else that plots a series of draws. Gradient/glow ids are namespaced per
// `uid` since several sparklines can render on the same page at once —
// unqualified ids would collide and every card would pick up the first
// one's fill.
import React, { useState, useEffect, useRef } from 'react';

// `labels` (optional, one string per point) shows as a hover tooltip on each
// dot — callers pass the draw date + value so a trend can be read per draw.
export default function Sparkline({ points, color, width = 200, height = 48, uid, labels }) {
  // Hover shows a dot's label on desktop; tap/click pins it for touch screens
  // (which have no hover). Tapping anywhere outside the chart clears it.
  const [active, setActive] = useState(null);
  const svgRef = useRef(null);
  useEffect(() => {
    if (active == null) return undefined;
    const away = (e) => { if (!svgRef.current?.contains(e.target)) setActive(null); };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [active]);
  if (!points || points.length < 2) return null;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const pad = 4;
  const coords = points.map((v, i) => ({
    x: pad + (i / (points.length - 1)) * (width - pad * 2),
    y: pad + (1 - (v - min) / range) * (height - pad * 2),
  }));
  const coordsStr = coords.map(p => `${p.x},${p.y}`).join(' L ');
  const linePathD = `M ${coordsStr}`;
  const last = coords[coords.length - 1];
  const areaPathD = `M ${pad},${height - pad} L ${coordsStr} L ${last.x},${height - pad} Z`;
  const fillId = `sparkFill-${uid}`;
  const glowId = `sparkGlow-${uid}`;

  return (
    // viewBox + max-width:100%/height:auto (rather than a bare width={width}
    // attribute) so the chart SHRINKS to fit a narrower container instead of
    // rendering at its full intrinsic width and spilling out — a marker
    // card nested several levels deep (results screen's "Key findings" box)
    // is narrower than this chart's default 200-260px on a phone-width
    // screen, and overflow:visible alone doesn't stop that, it just lets
    // the excess render outside the card border instead of clipping it.
    // Where a container already has room (e.g. the Current Markers tab),
    // max-width:100% never engages and nothing changes.
    <svg ref={svgRef} viewBox={`0 0 ${width} ${height}`} width={width} height={height}
      style={{ maxWidth: '100%', height: 'auto', overflow: 'visible' }}>
      <defs>
        <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.5" />
          <stop offset="45%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
        <radialGradient id={glowId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={color} stopOpacity="var(--chart-glow-inner)" />
          <stop offset="60%" stopColor={color} stopOpacity="var(--chart-glow-mid)" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </radialGradient>
      </defs>
      <path d={areaPathD} fill={`url(#${fillId})`} />
      <path d={linePathD} fill="none" stroke={color} strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round" />
      {coords.slice(0, -1).map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="4.5" fill="var(--bg-surface)" stroke={color} strokeWidth="2" />
          {labels?.[i] && (
            // Larger transparent target so the small dot is easy to hover or tap.
            <circle cx={p.x} cy={p.y} r="11" fill="transparent" style={{ cursor: 'pointer' }}
              onMouseEnter={() => setActive(i)} onMouseLeave={() => setActive(null)}
              onClick={() => setActive(i)} />
          )}
        </g>
      ))}
      <circle cx={last.x} cy={last.y} r={7 * 3.4} fill={`url(#${glowId})`} pointerEvents="none" />
      <circle cx={last.x} cy={last.y} r="7" fill={color} style={{ cursor: labels ? 'pointer' : undefined }}
        onMouseEnter={() => labels && setActive(coords.length - 1)} onMouseLeave={() => setActive(null)}
        onClick={() => labels && setActive(coords.length - 1)} />
      {active != null && labels?.[active] && (() => {
        const p = coords[active];
        const text = labels[active];
        const w = text.length * 5.6 + 14;
        const x = Math.min(Math.max(p.x - w / 2, 0), Math.max(width - w, 0));
        const above = p.y - 30 >= -8;
        const y = above ? p.y - 30 : p.y + 14;
        return (
          <g pointerEvents="none">
            <rect x={x} y={y} width={w} height="20" rx="5"
              fill="var(--bg-surface)" stroke="var(--border)" />
            <text x={x + w / 2} y={y + 13.5} textAnchor="middle" fontSize="10"
              fill="var(--text-primary)" style={{ fontFamily: 'var(--font-body)' }}>{text}</text>
          </g>
        );
      })()}
    </svg>
  );
}
